import React, { useEffect, useId } from "react";
import { PiWarningCircleFill } from "react-icons/pi";
import { useHotkeys } from "react-hotkeys-hook";
import {
  handleIsTopModal,
  handlePopModal,
  handlePushModal,
} from "../../utils/constants/CustomModalStack";
import CircularProgress from "@mui/material/CircularProgress";

const CustomModal = ({
  open,
  title,
  className,
  children,
  isLoading,
  isConfirmation,
  onClose,
  isButtonsRequired = true,
  bodyClassName = "",
  handleConfirmation,
  handleConfirmationBtnText = "Confirm",
}) => {
  const modalId = useId(); // unique id of a modal

  // Register/unregister modal from stack
  useEffect(() => {
    if (open) handlePushModal({ id: modalId });
    return () => handlePopModal({ id: modalId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Confirmation with Enter
  useHotkeys(
    "enter",
    (e) => {
      e.preventDefault();
      if (open && handleIsTopModal({ id: modalId }) && handleConfirmation)
        handleConfirmation();
    },
    { enabled: open && !isLoading }
  );

  // Close with Esc
  useHotkeys(
    "esc",
    (e) => {
      e.preventDefault();
      if (open && handleIsTopModal({ id: modalId }) && onClose) onClose();
    },
    { enabled: open && !isLoading }
  );

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[999] bg-gray-800 bg-opacity-50 flex items-center justify-center">
      <div
        className={` w-[95%] bg-white rounded-lg shadow-lg flex flex-col md:w-[60%] lg:w-[30%] max-h-[95vh] ${
          className || ""
        }`}
      >
        {/* Modal Header */}
        {title && (
          <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-start gap-x-3">
            <h2 className="text-[17px] font-semibold line-clamp-2 capitalize flex gap-x-2 items-center">
              {isConfirmation && (
                <PiWarningCircleFill className="text-red-500 text-[24px]" />
              )}
              {title}
            </h2>
            {onClose && (
              <button
                onClick={onClose}
                className="text-textBlack !font-semibold"
              >
                ✕
              </button>
            )}
          </div>
        )}

        {/* Modal Body (Scrollable) */}
        <div className={`flex-1 overflow-y-auto px-4 py-2 ${bodyClassName}`} id="HideScroll">{children}</div>

        {/* Modal Footer */}
        {isButtonsRequired && (
          <div className="px-6 py-3 bg-gray-100 flex justify-end space-x-3 rounded-b-md border-t">
            {onClose && (
              <button
                onClick={onClose}
                className="px-4 py-2 border !bg-white rounded-md font-semibold text-[14px]"
              >
                Cancel
              </button>
            )}
            <button
              onClick={handleConfirmation}
              disabled={isLoading}
              className={`px-4 py-2 text-white rounded-md text-[14px] font-semibold ${
                isConfirmation ? "bg-red-600" : "bg-primaryPurple"
              } `}
            >
              {isLoading ? (
                <div className="h-[25px]">
                  <CircularProgress className="!text-white mt-1" size={20} />
                  <p className="opacity-0">{handleConfirmationBtnText}</p>
                </div>
              ) : (
                handleConfirmationBtnText
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomModal;
